import React, {useCallback} from 'react';
import {assert} from 'assert-ts';
import {Concept} from 'types';
import type {VerifiedLinkedValueLink} from 'schemaDefinition/types';
import type {AgentWorkSummary} from 'services/agent/types';
import {useLocalization} from 'localization';
import {CodeListsGate} from 'services/codelists';
import {Builder, isAgentValue} from 'services/utils';
import {FlexBox} from 'components';
import {Preview, useSchemaCodelistIds} from 'schema';
import {entityTypeToSchemaReference} from 'schemas/functions/entityTypeToSchemaReference';
import {useEntitySchema, usePreviewConfiguration} from 'schemas/hooks';
import {AgentWorksTable} from 'widgets/agentWorks';
import type {EntityPreviewContent} from './types';
import {useEntitySearchContext} from '../context';
import {getEntityTitle} from '../context/functions/getEntityTitle';

export const AgentPreviewContent: EntityPreviewContent = ({
  entityType,
  entityValue,
  configuration,
}) => {
  const {t} = useLocalization();
  const schema = useEntitySchema(entityType);
  const codeLists = useSchemaCodelistIds(schema);

  const previewConfig = usePreviewConfiguration(
    entityTypeToSchemaReference(entityType),
    configuration,
  );

  const {navigateTo} = useEntitySearchContext();

  const agentValue = isAgentValue(entityValue) ? entityValue : undefined;

  const onNavigate = useCallback(
    (link: VerifiedLinkedValueLink) => {
      const backTitle = getEntityTitle(
        agentValue,
        Concept.person,
        agentValue?.id,
      );
      const backType = t(`general.entity.${Concept.person}`);
      const backLabel =
        new Builder(backType).add(': ', backTitle).value ?? t('general.back');
      navigateTo(link, backLabel);
    },
    [agentValue, navigateTo, t],
  );

  const onRowClick = useCallback(
    ({id, preferredTitle}: Pick<AgentWorkSummary, 'id' | 'preferredTitle'>) => {
      onNavigate({
        linkStatus: 'verified',
        entityId: id,
        type: Concept.work,
        name: (preferredTitle ?? [])?.join(', '),
      });
    },
    [onNavigate],
  );

  return (
    <CodeListsGate codeLists={codeLists}>
      <FlexBox>
        <Preview
          schema={assert(schema, 'EntityPreview: schema is not defined')}
          data={entityValue}
          configuration={previewConfig}
        />
        {isAgentValue(entityValue) ? (
          <AgentWorksTable agent={entityValue} onRowClick={onRowClick} />
        ) : null}
      </FlexBox>
    </CodeListsGate>
  );
};
